import React, { useState, useEffect, useRef, useCallback } from 'react';

interface TopProgressBarProps {
  loading: boolean;
}

export default function TopProgressBar({ loading }: TopProgressBarProps) {
  const [progress, setProgress] = useState(0);
  const [visible, setVisible] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const start = useCallback(() => {
    setVisible(true);
    setProgress(0);
    // Simulate progress — fast at first, then slow
    let p = 0;
    intervalRef.current = setInterval(() => {
      p += (90 - p) * 0.08;
      setProgress(Math.min(p, 90));
    }, 100);
  }, []);

  const finish = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    setProgress(100);
    timeoutRef.current = setTimeout(() => {
      setVisible(false);
      setProgress(0);
    }, 300);
  }, []);

  useEffect(() => {
    if (loading) {
      start();
    } else if (visible) {
      finish();
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  if (!visible) return null;

  return (
    <div className="top-progress-bar" aria-hidden="true">
      <div
        className="bar"
        style={{
          width: `${progress}%`,
          opacity: progress >= 100 ? 0 : 1,
          transition: progress >= 100
            ? 'width 0.2s ease, opacity 0.3s ease 0.1s'
            : 'width 0.3s ease',
        }}
      />
    </div>
  );
}
